import type { ReactNode } from 'react'
import * as T from '@radix-ui/react-tooltip'
import { cn } from './cn'
import { Kbd } from './kbd'

/** Mounted once by every shell (and the kit). Tooltips appear after 400 ms, instantly when moving between triggers. */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return <T.Provider delayDuration={400} skipDelayDuration={250}>{children}</T.Provider>
}

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  /** Combo shown as keycaps, e.g. "mod+k" or "g o". */
  shortcut?: string
  /** Controlled open (rare). */
  open?: boolean
  disabled?: boolean
  className?: string
}

/**
 * `<Tooltip content="Copy link"><IconButton …/></Tooltip>` — the child must accept a ref
 * (every kit control does). Tooltips never hold essential information: keep labels on controls.
 */
export function Tooltip({ content, children, side = 'top', align = 'center', shortcut, open, disabled, className }: TooltipProps) {
  if (disabled || (!content && !shortcut)) return <>{children}</>
  return (
    <T.Root open={open}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-[70] flex max-w-72 items-center gap-2 rounded-md bg-[#18181d] px-2 py-1 text-xs font-medium text-white shadow-popover',
            'dark:bg-[#2a2a31] data-[state=delayed-open]:animate-fade-in data-[state=instant-open]:animate-fade-in data-[state=closed]:animate-fade-out',
            className,
          )}
        >
          <span>{content}</span>
          {shortcut && <Kbd combo={shortcut} tone="inverse" />}
        </T.Content>
      </T.Portal>
    </T.Root>
  )
}
